import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, authApi } from '@/lib/api'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

interface RevenueData {
  totals: { paying: number; mrr: number; arpu: number }
  byPlan: { plan: string; count: number; mrr: number }[]
  byStatus: { status: string; count: number; mrr: number }[]
  pendingCancellations: number
}

interface BackfillResult {
  success: boolean
  totalSubscriptions: number
  updated: number
  notFound: number
  errors?: string[]
}

export function RevenuePage() {
  const queryClient = useQueryClient()
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<BackfillResult | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-analytics-revenue'],
    queryFn: () => api.get<RevenueData>('/api/admin/analytics/revenue'),
  })

  async function handleSync() {
    setSyncing(true)
    setSyncResult(null)
    try {
      const result = await authApi.post<BackfillResult>('/api/v1/stripe/backfill-subscriptions')
      setSyncResult(result)
      // Refresh revenue data
      queryClient.invalidateQueries({ queryKey: ['admin-analytics-revenue'] })
      queryClient.invalidateQueries({ queryKey: ['admin-analytics-overview'] })
    } catch (e) {
      setSyncResult({ success: false, totalSubscriptions: 0, updated: 0, notFound: 0, errors: [String(e)] })
    } finally {
      setSyncing(false)
    }
  }

  if (isLoading) {
    return <div className="flex items-center justify-center py-24"><LoadingSpinner size="lg" /></div>
  }

  const t = data?.totals

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-semibold">Revenue</h1>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="px-4 py-2 text-sm rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] disabled:opacity-50 transition-colors cursor-pointer"
        >
          {syncing ? 'Syncing...' : 'Sync Stripe Data'}
        </button>
      </div>

      {syncResult && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${syncResult.success ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-red-500/10 text-red-600 dark:text-red-400'}`}>
          {syncResult.success
            ? `Synced ${syncResult.updated} of ${syncResult.totalSubscriptions} subscriptions (${syncResult.notFound} not found in DB)`
            : `Sync failed: ${syncResult.errors?.join(', ')}`
          }
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="MRR" value={t?.mrr ? `$${(t.mrr / 100).toFixed(2)}` : '$0'} />
        <KpiCard label="Paying Users" value={t?.paying?.toLocaleString() ?? '0'} />
        <KpiCard label="ARPU" value={t?.arpu ? `$${(t.arpu / 100).toFixed(2)}` : '$0'} />
        <KpiCard
          label="Pending Cancellations"
          value={data?.pendingCancellations?.toString() ?? '0'}
          warn={!!data?.pendingCancellations}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* By Plan */}
        <Card>
          <CardHeader>
            <h3 className="font-heading font-semibold text-sm">Revenue by Plan</h3>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data?.byPlan ?? []).length === 0 ? (
              <p className="text-sm text-[var(--content-tertiary)]">No plan data</p>
            ) : (
              data?.byPlan.map((p) => (
                <div key={p.plan} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span>{p.plan || 'Unknown'}</span>
                    <Badge variant="default">{p.count} users</Badge>
                  </div>
                  <span className="tabular-nums font-medium">
                    ${(p.mrr / 100).toFixed(2)}/mo
                  </span>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* By Status */}
        <Card>
          <CardHeader>
            <h3 className="font-heading font-semibold text-sm">Users by Status</h3>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data?.byStatus ?? []).length === 0 ? (
              <p className="text-sm text-[var(--content-tertiary)]">No status data</p>
            ) : (
              data?.byStatus.map((s) => {
                const st = s.status?.toLowerCase()
                const variant = st === 'active' ? 'success' : (st === 'canceled' || st === 'unpaid' || st === 'incomplete_expired') ? 'error' : 'warning'
                return (
                  <div key={s.status} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant={variant}>{s.status || 'Unknown'}</Badge>
                      <span className="text-[var(--content-secondary)]">{s.count} users</span>
                    </div>
                    <span className="tabular-nums">
                      ${(s.mrr / 100).toFixed(2)}/mo
                    </span>
                  </div>
                )
              })
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function KpiCard({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <Card>
      <CardContent className="py-3">
        <p className="text-xs text-[var(--content-tertiary)] uppercase tracking-wider mb-1">{label}</p>
        <p className={`text-xl font-heading font-semibold tabular-nums ${warn ? 'text-amber-600 dark:text-amber-400' : ''}`}>
          {value}
        </p>
      </CardContent>
    </Card>
  )
}
